import React, { useMemo, useState } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { MONTH_NAMES_ES, DAY_OF_WEEK_NAMES_ES } from '../utils/dateBucketFormat';
import { DEFAULT_EVENT_COLOR, contrastTextColor } from '../utils/calendarEventColors';
import { resolveNextOrOngoingEvent, formatFriendlyDate } from '../utils/calendarNextEvent';

const WEEKDAY_SHORT = DAY_OF_WEEK_NAMES_ES.slice(1).map((d) => d.slice(0, 3).toUpperCase());

function pad2(n) {
  return String(n).padStart(2, '0');
}

function daysInMonth(year, monthIndex0) {
  return new Date(year, monthIndex0 + 1, 0).getDate();
}

/** 1 = Lunes ... 7 = Domingo, para alinear con DAY_OF_WEEK_NAMES_ES. */
function isoDow(year, monthIndex0, day) {
  const jsDow = new Date(year, monthIndex0, day).getDay();
  return jsDow === 0 ? 7 : jsDow;
}

/** Grilla del mes: null para los espacios antes/despues del mes, para completar semanas de 7. */
function buildMonthGrid(year, monthIndex0) {
  const totalDays = daysInMonth(year, monthIndex0);
  const firstDow = isoDow(year, monthIndex0, 1);
  const cells = [];
  for (let i = 1; i < firstDow; i += 1) cells.push(null);
  for (let day = 1; day <= totalDays; day += 1) cells.push(day);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

/** Fecha de inicio/fin de un evento, con compatibilidad hacia atrás con el campo antiguo `date` (fecha única). */
function eventStart(ev) {
  return ev.startDate || ev.date || '';
}
function eventEnd(ev) {
  return ev.endDate || ev.date || ev.startDate || '';
}

/** Lista de fechas YYYY-MM-DD entre inicio y fin (inclusive), para eventos de varios días. */
function eachDateInRange(startStr, endStr) {
  const start = new Date(`${startStr}T00:00:00`);
  const end = new Date(`${endStr}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return [];
  const dates = [];
  const cur = new Date(start);
  while (cur <= end) {
    dates.push(`${cur.getFullYear()}-${pad2(cur.getMonth() + 1)}-${pad2(cur.getDate())}`);
    cur.setDate(cur.getDate() + 1);
  }
  return dates;
}

/** Exporta el mismo listado de eventos como filas para Excel. */
export function calendarRowsForExport(events) {
  return (events || [])
    .slice()
    .sort((a, b) => eventStart(a).localeCompare(eventStart(b)))
    .map((ev) => ({
      Nombre: ev.name,
      Descripción: ev.description || '',
      'Fecha inicio': eventStart(ev),
      'Fecha fin': eventEnd(ev),
    }));
}

/**
 * Vista de calendario mensual para el dashboard: marca los feriados/eventos
 * configurados manualmente (chart.calendarEvents) sobre la grilla del mes.
 */
const CalendarWidget = ({ events = [] }) => {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [monthIndex0, setMonthIndex0] = useState(today.getMonth());

  const eventsByDate = useMemo(() => {
    const map = new Map();
    (events || []).forEach((ev) => {
      const start = eventStart(ev);
      if (!start) return;
      eachDateInRange(start, eventEnd(ev)).forEach((dateStr) => {
        const list = map.get(dateStr) || [];
        list.push(ev);
        map.set(dateStr, list);
      });
    });
    return map;
  }, [events]);

  const cells = useMemo(() => buildMonthGrid(year, monthIndex0), [year, monthIndex0]);
  const todayStr = `${today.getFullYear()}-${pad2(today.getMonth() + 1)}-${pad2(today.getDate())}`;

  const { ongoing, upcoming } = useMemo(() => resolveNextOrOngoingEvent(events, todayStr), [events, todayStr]);

  // Eventos que tocan el mes visible (no solo los que empiezan ahi: uno de varios dias
  // puede haber arrancado el mes anterior y seguir vigente), para la lista de solo
  // lectura debajo de la grilla. No reusa eventsByDate porque ese mapa es por dia
  // (un mismo evento aparece repetido ahi si dura varios dias).
  const monthEvents = useMemo(() => {
    const monthStart = `${year}-${pad2(monthIndex0 + 1)}-01`;
    const monthEnd = `${year}-${pad2(monthIndex0 + 1)}-${pad2(daysInMonth(year, monthIndex0))}`;
    return (events || [])
      .map((ev) => ({ ...ev, startDate: eventStart(ev), endDate: eventEnd(ev) }))
      .filter((ev) => ev.startDate && ev.startDate <= monthEnd && ev.endDate >= monthStart)
      .sort((a, b) => a.startDate.localeCompare(b.startDate));
  }, [events, year, monthIndex0]);

  const goPrev = () => {
    if (monthIndex0 === 0) {
      setMonthIndex0(11);
      setYear((y) => y - 1);
    } else {
      setMonthIndex0((m) => m - 1);
    }
  };
  const goNext = () => {
    if (monthIndex0 === 11) {
      setMonthIndex0(0);
      setYear((y) => y + 1);
    } else {
      setMonthIndex0((m) => m + 1);
    }
  };

  const banner = ongoing
    ? { label: 'Hoy', ev: ongoing }
    : upcoming
      ? { label: 'Próximo', ev: upcoming }
      : null;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 1 }}>
      {banner && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            mb: 1,
            px: 1,
            py: 0.5,
            borderRadius: 1,
            bgcolor: 'action.hover',
          }}
        >
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: banner.ev.color || DEFAULT_EVENT_COLOR, flexShrink: 0 }} />
          <Typography variant="caption" noWrap>
            <strong>{banner.label}:</strong> {banner.ev.name}
            {banner.label === 'Próximo' ? ` — ${formatFriendlyDate(banner.ev.startDate)}` : ''}
          </Typography>
        </Box>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
        <IconButton size="small" onClick={goPrev} title="Mes anterior">
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', minWidth: 140, textAlign: 'center' }}>
          {MONTH_NAMES_ES[monthIndex0 + 1]} {year}
        </Typography>
        <IconButton size="small" onClick={goNext} title="Mes siguiente">
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, flexShrink: 0 }}>
        {WEEKDAY_SHORT.map((d) => (
          <Typography key={d} variant="caption" color="text.secondary" sx={{ textAlign: 'center', fontWeight: 'bold' }}>
            {d}
          </Typography>
        ))}
        {cells.map((day, i) => {
          if (day == null) return <Box key={`empty-${i}`} />;
          const dateStr = `${year}-${pad2(monthIndex0 + 1)}-${pad2(day)}`;
          const dayEvents = eventsByDate.get(dateStr) || [];
          const hasEvent = dayEvents.length > 0;
          const isToday = dateStr === todayStr;
          // Si el dia tiene mas de un evento con colores distintos, se usa el del primero
          // (mismo criterio que el tooltip, que tampoco distingue cual es "el principal").
          const eventColor = dayEvents[0]?.color || DEFAULT_EVENT_COLOR;
          const cell = (
            <Box
              sx={{
                textAlign: 'center',
                borderRadius: 1,
                py: 0.5,
                fontSize: '0.8rem',
                border: isToday ? '1px solid' : '1px solid transparent',
                borderColor: isToday ? 'primary.main' : 'transparent',
                bgcolor: hasEvent ? eventColor : 'transparent',
                color: hasEvent ? contrastTextColor(eventColor) : 'text.primary',
              }}
            >
              {day}
            </Box>
          );
          return (
            <Box key={dateStr}>
              {hasEvent ? <Tooltip title={dayEvents.map((e) => e.name).join(', ')}>{cell}</Tooltip> : cell}
            </Box>
          );
        })}
      </Box>
      {monthEvents.length > 0 && (
        <Box sx={{ mt: 1, flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {monthEvents.map((ev) => {
            const dateLabel =
              ev.endDate && ev.endDate !== ev.startDate
                ? `${formatFriendlyDate(ev.startDate)} → ${formatFriendlyDate(ev.endDate)}`
                : formatFriendlyDate(ev.startDate);
            return (
              <Box key={ev.id || `${ev.name}-${ev.startDate}`} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, py: 0.25 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: ev.color || DEFAULT_EVENT_COLOR,
                    flexShrink: 0,
                    mt: 0.5,
                  }}
                />
                <Typography variant="caption" sx={{ lineHeight: 1.3 }}>
                  <strong>{ev.name}</strong> — {dateLabel}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default CalendarWidget;
